/**
 * Cross-browser rounded rectangle path.
 * ctx.roundRect() is Chrome 99+; this falls back to arcTo() on older browsers.
 * Always call ctx.beginPath() before and ctx.fill()/ctx.stroke() after.
 */
export function ctxRoundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  if (typeof (ctx as any).roundRect === "function") {
    (ctx as any).roundRect(x, y, w, h, r);
    return;
  }
  const radius = Math.min(r, w / 2, h / 2);
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

// MA colors
export const MA_COLORS = ["#38bdf8", "#f97316", "#a855f7"];

// Smooth easing functions
export const easeOutQuart = (x: number): number => {
  return 1 - Math.pow(1 - x, 4);
};

export const easeOutCubic = (x: number): number => {
  return 1 - Math.pow(1 - x, 3);
};

export const easeOutQuint = (x: number): number => {
  return 1 - Math.pow(1 - x, 5);
};

export const easeInOutQuart = (x: number): number => {
  return x < 0.5 ? 8 * x * x * x * x : 1 - Math.pow(-2 * x + 2, 4) / 2;
};

export const easeOutBack = (x: number): number => {
  const c1 = 3;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
};

// Draw SVG flag helper
export const drawSvgFlag = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  timeText?: string,
  isMobile: boolean = false,
  transparent: boolean = false,
  isCopyTrade: boolean = false,
) => {
  if (!timeText) return;

  const radius = isMobile ? 16 : 20;
  const circleX = x;
  const circleY = y + radius + 4;

  ctx.save();
  ctx.globalAlpha = 1;

  if (isCopyTrade) {
    // Copy trade: black circle with orange border and orange text
    ctx.beginPath();
    ctx.arc(circleX, circleY, radius, 0, Math.PI * 2);
    ctx.fillStyle = "#000000";
    ctx.fill();
    ctx.strokeStyle = "#F89A09";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.fillStyle = "#F4A425";
  } else if (transparent) {
    // Black-filled circle with white border and white text (bet flags)
    ctx.fillStyle = "#000000";
    ctx.beginPath();
    ctx.arc(circleX, circleY, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#FFFFFF";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.fillStyle = "#FFFFFF";
  } else {
    // Solid white circle with black text (selectedTimePeriod preview flag)
    ctx.fillStyle = "#FFFFFF";
    ctx.globalAlpha = 0.9;
    ctx.beginPath();
    ctx.arc(circleX, circleY, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.fillStyle = "#000000";
  }

  ctx.font = `bold ${isMobile ? 9 : 11}px monospace`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(timeText, circleX, circleY);

  ctx.restore();
};

export const isSameDayInTz = (a: Date, b: Date, timezone?: string): boolean => {
  if (!timezone) {
    return a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  }
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return fmt.format(a) === fmt.format(b);
};

export const formatTime = (
  date: Date,
  showSeconds: boolean,
  currentTimestamp?: number,
  dateOnly?: boolean,
  timezone?: string,
): string => {
  if (!Number.isFinite(date.getTime())) {
    return "";
  }

  let day: string, month: string, hours: string, minutes: string, seconds: string;

  if (timezone) {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: timezone,
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(date);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "00";
    day = get("day");
    month = get("month");
    hours = get("hour");
    minutes = get("minute");
    seconds = get("second");
  } else {
    day = String(date.getDate()).padStart(2, "0");
    month = String(date.getMonth() + 1).padStart(2, "0");
    hours = String(date.getHours()).padStart(2, "0");
    minutes = String(date.getMinutes()).padStart(2, "0");
    seconds = String(date.getSeconds()).padStart(2, "0");
  }

  if (dateOnly) {
    return `${day}.${month}`;
  }

  const timeStr = showSeconds ? `${hours}:${minutes}:${seconds}` : `${hours}:${minutes}`;

  // Show date prefix when the label is from a different day than today
  if (currentTimestamp) {
    const now = new Date(currentTimestamp);
    if (!isSameDayInTz(date, now, timezone)) {
      return `${day}.${month} | ${timeStr}`;
    }
  }

  return timeStr;
};

/** Formats a timezone offset as `UTC+2` / `UTC+5:30` / `UTC`. */
export const formatUtcOffsetLabel = (timezone = "UTC", date = new Date()): string => {
  let rawOffset = "GMT";
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: timezone,
      timeZoneName: "shortOffset",
    }).formatToParts(date);
    rawOffset = parts.find((p) => p.type === "timeZoneName")?.value ?? "GMT";
  } catch {
    rawOffset = "GMT";
  }

  const match = rawOffset.match(/^(?:GMT|UTC)([+-])(\d{1,2})(?::(\d{2}))?$/i);
  if (match) {
    const sign = match[1];
    const hours = Number(match[2]);
    const mins = match[3] ? Number(match[3]) : 0;
    if (hours === 0 && mins === 0) return "UTC";
    if (mins === 0) return `UTC${sign}${hours}`;
    return `UTC${sign}${hours}:${String(mins).padStart(2, "0")}`;
  }
  if (/^(?:GMT|UTC)$/i.test(rawOffset)) return "UTC";
  return rawOffset.replace(/^GMT/i, "UTC");
};

export const formatClockWithOffset = (date: Date, timezone = "UTC"): string => {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: timezone,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(date);
  } catch {
    parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "UTC",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(date);
  }

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "00";
  return `${get("hour")}:${get("minute")}:${get("second")} ${formatUtcOffsetLabel(timezone, date)}`;
};

export const formatPriceValue = (price: number, precision?: number): string => {
  const decimals =
    precision ?? (Math.abs(price) < 1 ? 6 : Math.abs(price) < 100 ? 4 : Math.abs(price) < 1000 ? 3 : 2);
  return price.toFixed(decimals);
};

// Calculate nice interval for price grid lines — snaps to 1/2/2.5/5×10^n
export const calculateNicePriceInterval = (priceRange: number): number => {
  const roughInterval = priceRange / 5;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughInterval)));
  const normalized = roughInterval / magnitude;
  let nice: number;
  if (normalized <= 1) nice = 1;
  else if (normalized <= 2) nice = 2;
  else if (normalized <= 2.5) nice = 2.5;
  else if (normalized <= 5) nice = 5;
  else nice = 10;
  return nice * magnitude;
};

// Calculate nice interval for time labels
export const calculateNiceInterval = (rawInterval: number, showSeconds: boolean): number => {
  if (showSeconds) {
    if (rawInterval <= 2) return 1;
    else if (rawInterval <= 5) return 5;
    else if (rawInterval <= 10) return 10;
    else if (rawInterval <= 15) return 15;
    else if (rawInterval <= 30) return 30;
    else return 60;
  } else {
    if (rawInterval <= 60) return 60;
    else if (rawInterval <= 120) return 120;
    else if (rawInterval <= 300) return 300;
    else if (rawInterval <= 600) return 600;
    else if (rawInterval <= 900) return 900;
    else if (rawInterval <= 1800) return 1800;
    else if (rawInterval <= 3600) return 3600;
    else if (rawInterval <= 7200) return 7200;
    else if (rawInterval <= 14400) return 14400;
    else if (rawInterval <= 28800) return 28800;
    else if (rawInterval <= 43200) return 43200;
    else return 86400;
  }
};

// Generate candlestick data from price
export const generateCandlestick = (
  previousCandle: { open: number; high: number; low: number; close: number } | null,
  targetPrice: number,
) => {
  const open = previousCandle?.close || targetPrice;
  const close = targetPrice;

  // Add some realistic variation
  const volatility = Math.abs(close - open) * 0.3;
  const high = Math.max(open, close) + Math.random() * volatility;
  const low = Math.min(open, close) - Math.random() * volatility;

  return {
    open: Math.max(150, Math.min(200, open)),
    high: Math.max(150, Math.min(200, high)),
    low: Math.max(150, Math.min(200, low)),
    close: Math.max(150, Math.min(200, close)),
  };
};

// Shared utility for calculating padded price range
import { CHART_CONFIG } from "./chart-config";

export interface PriceRangeParams {
  visibleData: Array<{ price?: number; low?: number; high?: number }>;
  chartType: "line" | "candlestick" | "heikin-ashi" | "bars";
  verticalBias?: number;
}

export interface PriceRangeResult {
  minPrice: number;
  maxPrice: number;
  priceRange: number;
  paddedMinPrice: number;
  paddedMaxPrice: number;
  paddedPriceRange: number;
}

export const calculatePaddedPriceRange = (params: PriceRangeParams): PriceRangeResult => {
  const { visibleData, chartType, verticalBias = 0 } = params;

  if (visibleData.length === 0) {
    return {
      minPrice: 0,
      maxPrice: 100,
      priceRange: 100,
      paddedMinPrice: 0,
      paddedMaxPrice: 100,
      paddedPriceRange: 100,
    };
  }

  let minPrice: number;
  let maxPrice: number;

  if (chartType === "line") {
    const prices = visibleData
      .flatMap((d) => [d.price, d.high, d.low])
      .filter((p): p is number => typeof p === "number" && !isNaN(p));
    if (prices.length === 0) {
      return {
        minPrice: 0,
        maxPrice: 100,
        priceRange: 100,
        paddedMinPrice: 0,
        paddedMaxPrice: 100,
        paddedPriceRange: 100,
      };
    }
    minPrice = Math.min(...prices);
    maxPrice = Math.max(...prices);
  } else {
    const allPrices = visibleData.flatMap((d) => [d.low!, d.high!]).filter((p) => p !== undefined && !isNaN(p));
    if (allPrices.length === 0) {
      return {
        minPrice: 0,
        maxPrice: 100,
        priceRange: 100,
        paddedMinPrice: 0,
        paddedMaxPrice: 100,
        paddedPriceRange: 100,
      };
    }
    minPrice = Math.min(...allPrices);
    maxPrice = Math.max(...allPrices);
  }

  if (!Number.isFinite(minPrice) || !Number.isFinite(maxPrice)) {
    return {
      minPrice: 0,
      maxPrice: 100,
      priceRange: 100,
      paddedMinPrice: 0,
      paddedMaxPrice: 100,
      paddedPriceRange: 100,
    };
  }

  const priceRange = maxPrice - minPrice || 1;

  // Применяем минимальный диапазон для candlestick
  let effectiveRange = priceRange;
  if (chartType !== "line") {
    const avgPrice = (minPrice + maxPrice) / 2;
    const minPriceRange = avgPrice * CHART_CONFIG.padding.minRangePercentage;
    effectiveRange = Math.max(priceRange, minPriceRange);
  }

  // Добавляем padding: 10% от диапазона сверху и 10% снизу
  const padding = effectiveRange * CHART_CONFIG.padding.yAxisPercentage;
  let paddedMinPrice = minPrice - padding;
  const paddedMaxPrice = maxPrice + padding;
  let paddedPriceRange = paddedMaxPrice - paddedMinPrice;

  if (verticalBias > 0) {
    const biasedRange = paddedPriceRange / (1 - verticalBias);
    paddedMinPrice -= biasedRange - paddedPriceRange;
    paddedPriceRange = biasedRange;
  }

  return {
    minPrice,
    maxPrice,
    priceRange,
    paddedMinPrice,
    paddedMaxPrice,
    paddedPriceRange,
  };
};

/**
 * Snaps a real-world timestamp to the chart-time of the candle that was open at that moment.
 * Used for bet anchors so the badge always aligns with its candle's center, matching the
 * live-placement behaviour (which pins to lastDP.time).
 */
export function snapBetToCandle(tsMs: number, data: { timestamp: number; time: number }[], refIndex: number): number {
  if (data.length === 0) return refIndex;
  let lo = 0,
    hi = data.length - 1,
    found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (data[mid]!.timestamp <= tsMs) {
      found = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  if (found >= 0) return data[found]!.time;
  // Bet predates all loaded candles — fall back to first candle
  return data[0]!.time;
}

/**
 * Binary-search interpolation: converts a real-world timestamp (ms) to a chart
 * time coordinate by finding the two surrounding data points and interpolating.
 *
 * - If tsMs is within the data range: interpolates between the two neighbours.
 * - If tsMs > last point (fast bet response): returns refIndex (pins to last point).
 * - If tsMs < first point (rare): extrapolates backward from the first two points.
 */
export function tsToChartTime(tsMs: number, data: { timestamp: number; time: number }[], refIndex: number): number {
  if (data.length === 0) return refIndex;

  let lo = 0,
    hi = data.length - 1,
    found = -1;

  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (data[mid]!.timestamp <= tsMs) {
      found = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }

  if (found >= 0 && found < data.length - 1) {
    const p0 = data[found]!,
      p1 = data[found + 1]!;
    const span = p1.timestamp - p0.timestamp;
    const ratio = span > 0 ? (tsMs - p0.timestamp) / span : 0;
    return p0.time + ratio * (p1.time - p0.time);
  }

  if (found === data.length - 1) {
    return refIndex;
  }

  const p0 = data[0]!,
    p1 = data.length > 1 ? data[1]! : data[0]!;
  const span = p1.timestamp - p0.timestamp;
  const ratio = span > 0 ? (tsMs - p0.timestamp) / span : 0;

  return p0.time + ratio * (p1.time - p0.time);
}
